import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { Tooltip } from './Tooltip';
import { api } from '../lib/api';
import { AppIcon } from './AppIcon';

type Props = {
  eventId: string;
  name: string;
  disabled?: boolean;
  beforeDelete?: () => Promise<unknown>;
  onDeleted: () => void;
};

export function EventMenu({ eventId, name, disabled, beforeDelete, onDeleted }: Props) {
  const id = useId();
  const eventName = name.trim() || 'Untitled event';
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const action = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    action.current?.focus({ preventScroll: true });
    const outside = (event: Event) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  useEffect(() => {
    if (confirming) {
      dialog.current?.showModal();
      cancel.current?.focus({ preventScroll: true });
    }
  }, [confirming]);

  const dismiss = () => {
    if (busy) return;
    dialog.current?.close();
    setConfirming(false);
    trigger.current?.focus({ preventScroll: true });
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try { await beforeDelete?.(); }
    catch {
      setError('Could not finish saving your changes. Cancel and resolve the saving error before deleting this event.');
      setBusy(false);
      return;
    }
    try {
      await api.del(`/events/${eventId}`);
      onDeleted();
    } catch {
      setError('Could not delete this event. Check your connection and try again.');
      setBusy(false);
    }
  };

  return <div class="event-menu" ref={root}>
    <Tooltip text="Event actions"><button type="button" class="event-menu-trigger" ref={trigger} disabled={disabled}
      aria-label={`Event actions for ${eventName}`} aria-haspopup="menu"
      aria-expanded={open} aria-controls={`${id}-menu`}
      onClick={() => setOpen(!open)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (open) action.current?.focus(); else setOpen(true);
        }
      }}><AppIcon name="more" /></button></Tooltip>
    {open && <div class="event-menu-popover" role="menu" aria-label={`Actions for ${eventName}`} id={`${id}-menu`}>
      <button type="button" role="menuitem" class="event-menu-action" ref={action}
        onKeyDown={(event) => {
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) event.preventDefault();
          if (event.key === 'Tab') { setOpen(false); trigger.current?.focus(); }
        }}
        onClick={() => { setOpen(false); setError(null); setConfirming(true); }}>
        <AppIcon name="trash" /> Delete event
      </button>
    </div>}
    {confirming && <dialog class="event-delete-dialog" ref={dialog}
      aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
      onCancel={(event) => { event.preventDefault(); dismiss(); }}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}>
      <h2 id={`${id}-title`}>Delete “{eventName}”?</h2>
      <p id={`${id}-description`}>This removes the event and its registration page. Your spreadsheet and signed forms remain in Google Drive.</p>
      {error && <p class="alert" role="alert">{error}</p>}
      <div class="event-delete-actions">
        <button type="button" class="secondary" ref={cancel} disabled={busy} onClick={dismiss}>Cancel</button>
        <button type="button" class="danger-solid" disabled={busy} onClick={() => { void remove(); }}>{busy ? 'Deleting…' : 'Delete event'}</button>
      </div>
    </dialog>}
  </div>;
}
